import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  PaymentAccountBank,
  PaymentAccountBankResponseModel,
  PaymentAccountCreditCard,
  PaymentAccountCreditCardResponseModel,
  PaymentAccountUpi,
  PaymentAccountUpiResponseModel
} from '../models/payment-method.model';
import { UserResponseModel } from '../models/user.model';
import { ApiCollection } from '../providers/api-collection';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodsService {
  constructor(private apiService: ApiService) {}

  private request<T>(method: string, action: string, data: any = {}): Observable<T> {
    return this.apiService
      .execute(data, `${this.apiService.url}${action}`, method)
      .pipe(map((res: any) => res.res as T));
  }

  getBankAccounts(page: number = 1, pageSize: number = 200): Observable<PaymentAccountBankResponseModel> {
    return this.request<PaymentAccountBankResponseModel>('GET', `${ApiCollection.PaymentAccountBank}?page=${page}&page_size=${pageSize}`);
  }

  getBankAccountById(id: number): Observable<PaymentAccountBank> {
    return this.request<PaymentAccountBank>('GET', `${ApiCollection.PaymentAccountBank}/${id}`);
  }

  createBankAccount(payload: Partial<PaymentAccountBank>): Observable<PaymentAccountBank> {
    return this.request<PaymentAccountBank>('POST', ApiCollection.PaymentAccountBank, payload);
  }

  updateBankAccount(id: number, payload: Partial<PaymentAccountBank>): Observable<PaymentAccountBank> {
    return this.request<PaymentAccountBank>('PATCH', `${ApiCollection.PaymentAccountBank}/${id}`, payload);
  }

  deleteBankAccount(id: number): Observable<any> {
    return this.request<any>('DELETE', `${ApiCollection.PaymentAccountBank}/${id}`);
  }

  getCreditCards(page: number = 1, pageSize: number = 200): Observable<PaymentAccountCreditCardResponseModel> {
    return this.request<PaymentAccountCreditCardResponseModel>('GET', `${ApiCollection.PaymentAccountCreditCard}?page=${page}&page_size=${pageSize}`);
  }

  getCreditCardById(id: number): Observable<PaymentAccountCreditCard> {
    return this.request<PaymentAccountCreditCard>('GET', `${ApiCollection.PaymentAccountCreditCard}/${id}`);
  }

  createCreditCard(payload: Partial<PaymentAccountCreditCard>): Observable<PaymentAccountCreditCard> {
    return this.request<PaymentAccountCreditCard>('POST', ApiCollection.PaymentAccountCreditCard, payload);
  }

  updateCreditCard(id: number, payload: Partial<PaymentAccountCreditCard>): Observable<PaymentAccountCreditCard> {
    return this.request<PaymentAccountCreditCard>('PATCH', `${ApiCollection.PaymentAccountCreditCard}/${id}`, payload);
  }

  deleteCreditCard(id: number): Observable<any> {
    return this.request<any>('DELETE', `${ApiCollection.PaymentAccountCreditCard}/${id}`);
  }

  getUpiAccounts(page: number = 1, pageSize: number = 200): Observable<PaymentAccountUpiResponseModel> {
    return this.request<PaymentAccountUpiResponseModel>('GET', `${ApiCollection.PaymentAccountUpi}?page=${page}&page_size=${pageSize}`);
  }

  getUpiAccountById(id: number): Observable<PaymentAccountUpi> {
    return this.request<PaymentAccountUpi>('GET', `${ApiCollection.PaymentAccountUpi}/${id}`);
  }

  createUpiAccount(payload: Partial<PaymentAccountUpi>): Observable<PaymentAccountUpi> {
    return this.request<PaymentAccountUpi>('POST', ApiCollection.PaymentAccountUpi, payload);
  }

  updateUpiAccount(id: number, payload: Partial<PaymentAccountUpi>): Observable<PaymentAccountUpi> {
    return this.request<PaymentAccountUpi>('PATCH', `${ApiCollection.PaymentAccountUpi}/${id}`, payload);
  }

  deleteUpiAccount(id: number): Observable<any> {
    return this.request<any>('DELETE', `${ApiCollection.PaymentAccountUpi}/${id}`);
  }

  getUsers(page: number = 1, pageSize: number = 1000): Observable<UserResponseModel> {
    return this.request<UserResponseModel>('GET', `${ApiCollection.User}?page=${page}&page_size=${pageSize}&is_deleted=false`);
  }

  uploadMedia(file: File, createdBy: number): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('created_by', String(createdBy));

    return this.request<any>('POST', ApiCollection.Media, formData);
  }
}
